import React, { useState, useEffect } from "react";
import {
  Award,
  Building,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Flag,
  GraduationCap,
  Heart,
  Home,
  Mail,
  MapPin,
  Phone,
  Save,
  Shield,
  User,
  X,
} from "lucide-react";
import { getUserInitials } from "../../utils/avatarUtils";
import StatusBadge from "../common/StatusBadge";
import { apiFetch } from "../../../../context/AuthContext";
import { API_URL } from "../../config";
import { FIELD_SECTIONS } from "../../../(admin)employeemanagement/components/modals/EditEmployeeModal";

// Full employee field catalog — shared with the admin table so both view
// modals show every database field, plus a view-only system section.
const VIEW_EMPLOYEE_SECTIONS = [
  ...FIELD_SECTIONS,
  {
    key: "system",
    title: { en: "System Information", ar: "معلومات النظام" },
    fields: [
      { name: "infinityCode", label: { en: "Infinity Code", ar: "كود إنفينيتي" }, type: "text" },
      { name: "id", label: { en: "Database ID", ar: "معرف قاعدة البيانات" }, type: "text" },
      { name: "created_at", label: { en: "Created At", ar: "تاريخ الإنشاء" }, type: "date" },
      { name: "updated_at", label: { en: "Last Updated", ar: "آخر تحديث" }, type: "date" },
    ],
  },
];

const VIEW_SECTION_ICONS = {
  basic: User,
  address: Home,
  work: Building,
  management: Award,
  compensation: DollarSign,
  contract: FileText,
  insurance: Shield,
  education: GraduationCap,
  licenses: CheckCircle,
  documents: FileText,
  system: Clock,
};

// Fields rendered as currency in the view modal.
const VIEW_MONEY_FIELDS = new Set([
  "netSalary",
  "variableSalary",
  "transportationAllowances",
  "insuranceSalary",
]);

// ===== RESPONSIVE VIEW EMPLOYEE MODAL COMPONENT =====
const ViewEmployeeModal = ({
  styles,
  text,
  language,
  screenSize,
  fonts,
  spacing,
  isDarkMode,
  selectedEmployee,
  employeeProfilePics,
  onClose,
  documentChecklist,
  hasUnsavedChanges,
  savingDocument,
  onCheckboxChange,
  onSaveDocuments,
}) => {
  const infinityCode =
    selectedEmployee?.fullEmployeeData?.infinityCode ||
    selectedEmployee?.infinityCode ||
    selectedEmployee?.id ||
    null;

  // Fetch the COMPLETE record (all database fields); seed with local data.
  const [fullRecord, setFullRecord] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);
  useEffect(() => {
    if (!infinityCode) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await apiFetch(
          `${API_URL}/employees/select/${infinityCode}`,
          { method: "GET" }
        );
        const json = await response.json().catch(() => null);
        if (!cancelled && response.ok && json?.employee) {
          setFullRecord(json.employee);
        }
      } catch (err) {
        console.error("Failed to fetch full employee record:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [infinityCode]);

  // Return null if no employee is selected (after hooks)
  if (!selectedEmployee) return null;

  // Merge the fetched complete record over the locally loaded row data
  // (with aliases for keys the local mapper renamed).
  const local = selectedEmployee.fullEmployeeData || {};
  const employeeData = {
    ...local,
    dob: local.dateOfBirth,
    emergencyPhoneNumber: local.emergencyPhone,
    created_at: local.createdAt,
    updated_at: local.updatedAt,
    sr: selectedEmployee.name,
    email: selectedEmployee.email,
    phoneNumber: selectedEmployee.phone,
    employeeType: selectedEmployee.department,
    employeeState: selectedEmployee.status,
    hiringDate: selectedEmployee.joinDate,
    infinityCode: selectedEmployee.id,
    ...(fullRecord || {}),
  };

  // Function to format date strings
  const formatDate = (dateString) => {
    if (!dateString) return text[language].na;
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: screenSize === "xs" ? "short" : "long",
      day: "numeric",
    });
  };

  // Function to format currency amounts
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "" || amount === 0)
      return text[language].na;
    const numeric = Number(amount);
    if (isNaN(numeric)) return text[language].na;
    return `$${numeric.toLocaleString()}`;
  };

  const renderBooleanValue = (value) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: value ? "rgba(16,185,129,0.12)" : "rgba(148,163,184,0.12)",
        color: value ? "#059669" : isDarkMode ? "#94a3b8" : "#64748b",
      }}
    >
      {value
        ? language === "ar" ? "نعم ✓" : "Yes ✓"
        : language === "ar" ? "لا ✗" : "No ✗"}
    </span>
  );

  // Resolve a display value for any field of the shared catalog
  const renderFieldValue = (field) => {
    const value = employeeData?.[field.name];
    if (field.type === "boolean") return renderBooleanValue(Boolean(value));
    if (field.type === "date") return formatDate(value);
    if (field.type === "department")
      return String(
        employeeData?.department?.name ??
          selectedEmployee?.department ??
          text[language].na
      );
    if (VIEW_MONEY_FIELDS.has(field.name)) return formatCurrency(value);
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === "N/A"
    )
      return text[language].na;
    return String(value);
  };

  // Checklist items: document field ↔ translation key
  const checklistItems = [
    { field: "photosCount", labelKey: "photos" },
    { field: "birthCertificateCopy", labelKey: "birthForm" },
    { field: "gradCertificateOriginal", labelKey: "graduation" },
    { field: "idCopy", labelKey: "idCopy" },
    { field: "noCrimeCertificate", labelKey: "criminalRecord" },
    { field: "laborLetterOriginal", labelKey: "workCertificate" },
    { field: "medicalCheckForm111", labelKey: "form111" },
  ];

  // Return modal JSX
  return (
    <>
    <div
      style={styles.modal}
      onClick={onClose}
    >
      <div
        style={styles.viewModalContent}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{text[language].employeeDetails}</h2>
          <button
            style={styles.closeBtn}
            onClick={onClose}
          >
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Modal Body */}
        <div style={styles.viewModalBody}>
          {/* Employee Header Section */}
          <div style={{
            ...styles.employeeHeader,
            flexDirection: 'row',
            alignItems: 'center',
            textAlign: language === 'ar' ? 'right' : 'left'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.margin,
              flex: 1
            }}>
              {/* Profile Picture */}
              {employeeProfilePics[selectedEmployee.id] ? (
                <img
                  src={employeeProfilePics[selectedEmployee.id]}
                  alt={selectedEmployee.name}
                  style={{
                    ...styles.largeAvatar,
                    objectFit: 'cover',
                    cursor: 'pointer'
                  }}
                  onClick={() => setShowFullImage(true)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    if (e.target.nextSibling?.style) e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}

              <div
                style={{
                  ...styles.largeAvatar,
                  display: employeeProfilePics[selectedEmployee.id] ? 'none' : 'flex'
                }}
              >
                {getUserInitials(selectedEmployee?.name)}
              </div>

              {/* Employee Info */}
              <div style={{
                ...styles.employeeHeaderInfo,
                textAlign: language === 'ar' ? 'right' : 'left'
              }}>
                <h3 style={styles.employeeName}>{selectedEmployee?.name}</h3>
                <p style={styles.employeeTitle}>{selectedEmployee?.position}</p>
                <p style={styles.employeeDepartment}>
                  {selectedEmployee?.department}
                </p>
                <div style={styles.employeeStatus}>
                  <StatusBadge
                    status={selectedEmployee?.status}
                    screenSize={screenSize}
                    fonts={fonts}
                    text={text}
                    language={language}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* All database fields, section by section (shared catalog with the admin table) */}
          {VIEW_EMPLOYEE_SECTIONS.map((section) => {
            const SectionIcon = VIEW_SECTION_ICONS[section.key] || FileText;
            return (
              <div key={section.key} style={styles.infoSection}>
                <div style={styles.sectionHeader}>
                  <SectionIcon size={screenSize === "xs" ? 16 : 18} />
                  <span>{section.title[language] || section.title.en}</span>
                </div>
                <div style={styles.infoGrid}>
                  {section.fields.map((field) => (
                    <div key={field.name} style={styles.infoItem}>
                      <div style={{ width: "100%" }}>
                        <div style={styles.infoLabel}>
                          {field.label[language] || field.label.en}
                        </div>
                        <div style={styles.infoValue}>
                          {renderFieldValue(field)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Document Checklist Section */}
          <div style={styles.infoSection}>
            <div style={styles.sectionHeader}>
              <FileText size={screenSize === "xs" ? 16 : 18} />
              <span>{text[language].documentChecklist}</span>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: screenSize === 'xs' || screenSize === 'sm' ? '1fr' : 'repeat(2, 1fr)',
              gap: spacing.margin,
              padding: spacing.padding
            }}>
              {checklistItems.map(({ field, labelKey }) => (
                <label
                  key={field}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    cursor: 'pointer',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    transition: 'all 0.2s ease'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={documentChecklist[field]}
                    onChange={(e) => onCheckboxChange(field, e.target.checked)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#10b981'
                    }}
                  />
                  <span style={{
                    fontSize: fonts.body,
                    color: isDarkMode ? '#fff' : '#1f2937',
                    fontWeight: '500'
                  }}>
                    {text[language][labelKey]}
                  </span>
                  {documentChecklist[field] && (
                    <CheckCircle size={16} style={{ color: '#10b981', marginLeft: 'auto' }} />
                  )}
                </label>
              ))}
            </div>

            {/* Save Button for Document Checklist */}
            <div style={{
              padding: spacing.padding,
              paddingTop: 0,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: spacing.margin
            }}>
              <button
                onClick={onSaveDocuments}
                disabled={!hasUnsavedChanges || savingDocument}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: screenSize === 'xs' ? '0.75rem 1.5rem' : '1rem 2rem',
                  fontSize: fonts.body,
                  fontWeight: '600',
                  color: '#fff',
                  backgroundColor: hasUnsavedChanges ? '#10b981' : '#94a3b8',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: hasUnsavedChanges && !savingDocument ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  opacity: savingDocument ? 0.7 : 1
                }}
                onMouseEnter={(e) => {
                  if (hasUnsavedChanges && !savingDocument) {
                    e.target.style.backgroundColor = '#059669';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasUnsavedChanges && !savingDocument) {
                    e.target.style.backgroundColor = '#10b981';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }
                }}
              >
                {savingDocument ? (
                  <>
                    <Save size={18} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
                    {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Fullscreen image lightbox */}
    {showFullImage && employeeProfilePics[selectedEmployee.id] && (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 4000,
          padding: '2rem',
          cursor: 'zoom-out',
        }}
        onClick={() => setShowFullImage(false)}
      >
        <img
          src={employeeProfilePics[selectedEmployee.id]}
          alt={selectedEmployee.name}
          style={{
            width: 'min(70vw, 70vh, 32rem)',
            height: 'min(70vw, 70vh, 32rem)',
            borderRadius: '50%',
            objectFit: 'cover',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
            cursor: 'default',
          }}
          onClick={(e) => e.stopPropagation()}
        />
        <button
          onClick={() => setShowFullImage(false)}
          style={{
            position: 'fixed',
            top: '1.5rem',
            [language === 'ar' ? 'left' : 'right']: '1.5rem',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '50%',
            width: '2.75rem',
            height: '2.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <X size={24} />
        </button>
      </div>
    )}
    </>
  );
};

export default ViewEmployeeModal;
